import { Component, Input, OnInit } from '@angular/core';
import { WaterRequestService, DashboardRecord, WaterRequest, DashboardMatch, BidRecord} from '../../../../services/water-request-service';
import { DialogService } from '../../../utility/dialog/dialog-service';
import { Guid } from 'guid-typescript';
import { WaterRequestFormComponent } from '../ws-request-form/ws-request-form.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EditDialogService } from '../../../utility/dialog/edit-dialog-service';

interface SupplyRequest {
  padName: string;
  dateRange: string;
  status: string;
  rate: number;
  matches: number;
  recordId?: string;          
  clientId?: string;          
  dashboardRecord?: DashboardRecord;
  expanded?: boolean;
  selected?: boolean;
  mode: 'Share' | 'Trade';
  hasAdminApproval: boolean;
  clientName?: string;
  userName?: string;
}

@Component({
  selector: 'ws-dashboard',
  templateUrl: './ws-dashboard.component.html',
  standalone: false,
  styleUrls: ['./ws-dashboard.component.css']
})
export class WsDashboardComponent implements OnInit {
  @Input() viewType: 'Share' | 'Trade' | 'Admin' = 'Share';
  activeTab: 'supply' | 'demand' = 'supply';

  supplyRequests: SupplyRequest[] = [];
  demandRequests: SupplyRequest[] = [];
  // List of requests to display in the dashboard
  requests: BidRecord[] = [];

 
  supplyAction = '';
  demandAction = '';

  dialogType = 'Trade';
  dialog: any = { start_date: '', end_date: '', bid_type: '', bid_amount: '', bid_units: '' };

  constructor(private waterRequestService: WaterRequestService, private editDialogService: EditDialogService, private dialogService: MatDialog,) { }

  ngOnInit() {
    this.loadRequests();
  }

  loadRequests(): void {
    const haveNeed: 'Have' | 'Need' = this.activeTab === 'supply' ? 'Have' : 'Need';
    const isForApproval = this.viewType === 'Admin';

    const allRecords: DashboardRecord[] = [];

    const modes: ('Share' | 'Trade')[] = isForApproval
      ? ['Share', 'Trade']
      : [this.viewType === 'Trade' ? 'Trade' : 'Share'];

    let remaining = modes.length;

    modes.forEach(mode => {
      this.waterRequestService.dashboardData(
        mode,
        haveNeed,
        isForApproval,
        (records: DashboardRecord[]) => {
          const recordsWithMode = records.map(r => ({
            ...r,
            mode
          }));

          allRecords.push(...recordsWithMode);
          remaining--;

          if (remaining === 0) {
            const filteredRecords = allRecords.filter(r => {
              if (r.status === 'staging' && !isForApproval) return false;
              return true;
            });

            const mapped = filteredRecords.map(r => ({
              ...this.mapToUiModel(r),
              status: r.status,
              mode: r.mode
            }));

            if (this.activeTab === 'supply') {
              this.supplyRequests = mapped;
            } else {
              this.demandRequests = mapped;
            }
          }
        }
      );
    });
  }


  private mapToUiModel(r: DashboardRecord): SupplyRequest {
    const dates = r.dateRange?.split('-').map(d => new Date(d.trim()));
    const endDate = dates?.[1];

    return {
      padName: r.siteName,
      dateRange: r.dateRange,
      status: endDate && endDate >= new Date() ? 'Active' : 'Expired',
      rate: r.rateBpd,
      matches: r.matches?.length ?? 0,
      recordId: r.id.toString(),
      clientId: r.clientId,
      dashboardRecord: r,
      mode: r.mode,
      hasAdminApproval: r.hasAdminApproval,
      clientName: r.clientName,
      userName: r.userName
    };
  }


  closeDialog() { }

  submitDialog() {
  }

  
  viewMatches(supply: SupplyRequest) {
    if (!supply.dashboardRecord) return;
  }

  approveBid(bid: DashboardRecord, match: DashboardMatch) {
    if (!match.matchId) return;
    if (!bid.id) return;

    this.waterRequestService.approve(match.matchId, bid.id, () => {
      this.loadRequests();
    });
  }

  rejectBid(bid: DashboardRecord, match: DashboardMatch) {
    if (!match.matchId) return;
    if (!bid.id) return;

    this.waterRequestService.reject(match.matchId, bid.id, () => {
      this.loadRequests();
    });
  }



  adminApprove(supply: SupplyRequest) {
    if (!supply.recordId || !supply.clientId) return;

    const bidId = Guid.parse(supply.recordId);  
    this.waterRequestService.adminApproval(supply.clientId, bidId, () => {
      this.loadRequests();
    });
  }

  applySupplyAction() {
    if (!this.supplyAction) return;

    const bidIds: string[] = this.supplyRequests
      .filter(r => r.selected && r.recordId)
      .map(r => r.recordId!);

    if (!bidIds.length) return;

    this.waterRequestService.doAction(
      this.supplyAction,
      bidIds,
      () => {
        this.loadRequests();
        this.supplyAction = '';
      }
    );
  }

  applyDemandAction() {
    if (!this.demandAction) return;
    const bidIds: string[] = this.demandRequests
      .filter(r => r.selected && r.recordId)
      .map(r => r.recordId!);

    if (!bidIds.length) return;

    this.waterRequestService.doAction(
      this.demandAction,
      bidIds,
      () => {
        this.loadRequests();
        this.demandAction = '';
      }
    );
  }


  toggleExpand(supply: SupplyRequest) {
    supply.expanded = !supply.expanded;

    // Only view matches when expanding, not collapsing
    if (supply.expanded) {
      this.viewMatches(supply);
    }
  }

  get enableDemandApply(): boolean {
    if (!this.demandAction) return false;

    return this.demandRequests.some(r => r.selected);
  }

  get enableSupplyApply(): boolean {
    if (!this.supplyAction) return false;

    return this.supplyRequests.some(r => r.selected);
  }


  editRecord(item: SupplyRequest) {
    if (!item.recordId || !item.clientId) return;

    this.dialogService.open(WaterRequestFormComponent, {
      width: '90vw',
      maxWidth: '1200px',
      maxHeight: '80vh', 
      panelClass: 'full-dialog',
      data: {
        recordId: item.recordId,
        clientId: item.clientId,
        type: this.activeTab === 'supply' ? 'Have' : 'Need',
        //context: this.viewType === 'Trade' ? 'Trade' : 'Share'
        context: item.mode ?? 'Share' // use the stored mode
      }
    });
  }

  downloadCsv(record: DashboardRecord): void {
    if (!record?.matches?.length) return;

    const visibleMatches = record.matches.filter(m => m.showUserDetails);
    if (!visibleMatches.length) return;

    // CSV headers
    const csvRows: string[][] = [
      record.mode === 'Trade'
        ? [
          'Match Date',
          'Match Volume',
          'Match Price',
          'Match Value',
          'Match Balance Type',
          'Providing Trucking Volume',
          'Providing Trucking Price',
          'Providing Trucking Value',
          'Providing Piping Volume',
          'Providing Piping Price',
          'Providing Piping Value',
          'Obtaining Trucking Volume',
          'Obtaining Piping Volume',
          'Net Value',
          'Net Balance Type',
          'Quality'
        ]
        : [
          'Match Date',
          'Match Volume',
          'Providing Trucking Volume',
          'Providing Piping Volume',
          'Obtaining Trucking Volume',
          'Obtaining Piping Volume',
          'Quality'
        ]
    ];

    this.waterRequestService.recordInfo(record.id.toString(), record.clientId, ownBid => {
      const fetchMatchedBid = (
        match: DashboardMatch
      ): Promise<{ provide: BidRecord; need: BidRecord; match: DashboardMatch }> => {
        return new Promise(resolve => {
          this.waterRequestService.recordInfo(
            match.matchedBidId.toString(),
            match.matchedClientId,
            matchedBid => {
              const provide = ownBid.provideOrNeed === 'Have' ? ownBid : matchedBid;
              const need = ownBid.provideOrNeed === 'Have' ? matchedBid : ownBid;
              resolve({ provide, need, match });
            }
          );
        });
      };
      Promise.all(visibleMatches.map(fetchMatchedBid)).then(results => {
        if (!results.length) return;

        const earliestStart = new Date(
          Math.min(...visibleMatches.map(m => new Date(m.dateRange.split(' - ')[0]).getTime()))
        );
        const latestEnd = new Date(
          Math.max(...visibleMatches.map(m => new Date(m.dateRange.split(' - ')[1]).getTime()))
        );
        const totalDays = Math.floor((latestEnd.getTime() - earliestStart.getTime()) / 86400000) + 1;

        const { provide, need, match } = results[0];

        for (let i = 0; i < totalDays; i++) {
          const date = new Date(earliestStart);
          date.setDate(date.getDate() + i);

          if (record.mode === 'Trade') {
            const matchVolume = match.rateBpd ?? 0;
            const matchPrice = ownBid.bidAmount ?? 0;
            const matchValue = (match.totalVolume?? 0) * matchPrice;
            const matchBalanceType =
              ownBid.provideOrNeed === 'Have' ? 'Want to be paid' : 'Willing to pay';

            const providingTrucksVolume = provide.provideTrucksCapacity ?? '-';
            const providingTrucksPrice = provide.provideTrucksBid ?? '-';
            const providingTrucksValue =
              typeof providingTrucksVolume === 'number' && typeof providingTrucksPrice === 'number'
                ? providingTrucksVolume * providingTrucksPrice
                : '-';

            const providingPipingVolume = provide.provideLayflatsCapacity ?? '-';
            const providingPipingPrice = provide.provideLayflatsBid ?? '-';
            const providingPipingValue =
              typeof providingPipingVolume === 'number' && typeof providingPipingPrice === 'number'
                ? providingPipingVolume * providingPipingPrice
                : '-';

            const obtainingTrucksVolume = need.provideTrucksCapacity ?? '-';
            const obtainingPipingVolume = need.provideLayflatsCapacity ?? '-';

            const totalCost = match.totalCost ?? 0;
            const seller = provide; 
            const netValue = seller === provide ? totalCost : -totalCost;
            const netBalanceType =
              ownBid.provideOrNeed === 'Have' ? 'To be paid' : 'To pay';

            const row: (string | number)[] = [
              date.toLocaleDateString(),
              matchVolume,
              matchPrice,
              matchValue,
              matchBalanceType,
              providingTrucksVolume,
              providingTrucksPrice,
              providingTrucksValue,
              providingPipingVolume,
              providingPipingPrice,
              providingPipingValue,
              obtainingTrucksVolume,
              obtainingPipingVolume,
              netValue,
              netBalanceType,
              provide.waterQuality ?? 'Best match did not include quality details'
            ];

            csvRows.push(row.map(v => v.toString()));
          } else {
            const row: (string | number)[] = [
              date.toLocaleDateString(),
              match.rateBpd,
              ownBid.rate,
              provide.provideTrucksCapacity ?? '-',
              provide.provideLayflatsCapacity ?? '-',
              need.provideTrucksCapacity ?? '-',
              need.provideLayflatsCapacity ?? '-',
              provide.waterQuality ?? 'Best match did not include quality details'
            ];

            csvRows.push(row.map(v => v.toString()));
          }
        }

        // --- Export CSV ---
        const csv = csvRows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'dashboard_matches.csv';
        link.click();
      });
    });
  }

}
